import { navigate, type Screen } from '@/app'
import type { ContentCard, Home } from '@/lib/catalog'
import type { ContinueCard } from '@/lib/continue'
import { el } from '@/lib/dom'
import { T } from '@/lib/i18n'
import { KIDS_CATEGORIES, KIDS_ROW_TONES, kidsCategoryIcon } from '@/lib/kids'
import { readSelectedProfileName } from '@/lib/profiles'
import { openWebDetail } from '@/browser/detailModal'
import { webCard, webEmpty, webPage, webRow, webScreen } from '@/browser/ui'

/**
 * UŞAQ ANA EKRANI — BRAUZER VARİANTI (tv.kinobox.az).
 *
 * ⚠⚠ NİYƏ VAR (2026-08-30, TestingAI veb TV 67). NƏ ÖLÇÜLDÜ: veb TV-də uşaq profili
 *    «Ləman» (isKids=true, maxAgeRating=12) seçilib səhifə yeniləndi — səhnədə ADİ
 *    ana ekran çəkildi (ilk düyünün sinfi `web-page`, adi hero, «Sənin üçün / Yeni
 *    filmlər / Ən çox baxılanlar / Festival qutusu» sıraları, 163 `.web-card`),
 *    `/Salam|Nə baxaq/` naxışı səhnə mətnində TAPILMADI. ƏVVƏL NƏ OLURDU: uşaq
 *    ekranı yalnız cihaz giriş nöqtəsinə bağlı idi (`src/main.ts` → `homeScreen()`
 *    → `isKidsProfile() ? createKidsHomeScreen : createHomeScreen`), brauzer giriş
 *    nöqtəsi isə həmişə `createWebHomeScreen` çağırırdı.
 *
 * ⚠⚠ NİYƏ CİHAZ EKRANI OLDUĞU KİMİ İŞLƏDİLMƏDİ: `screens/kidsHome.ts` `FocusGrid`
 *    üzərində qurulub və hər blokun `top`-unu SABİT piksellə (`GREETING_HEIGHT`
 *    200 · `ROW_HEIGHT` 450 · `CARD_PITCH` 244) 1920×1080 kətan üçün hesablayır;
 *    brauzer variantı isə məhz o mühərriki İŞLƏTMİR (`browser/main.ts` başlığı:
 *    «`lib/focus.ts` şəbəkəsi … sabit 1920×1080 kətan üçün yazılıb və bu variantda
 *    mənasızdır») — orada sürüşmə DOĞMADIR və pəncərənin eni dəyişir.
 *
 * ⚠ TƏKRAR YOXDUR, MAKET AYRIDIR: janr siyahısı, ikon yolları və rəng dövrü ORTAQ
 *   moduldadır (`lib/kids.ts`), rənglərin özü isə `tv.css`-dəki `[data-kids]`
 *   qaydalarındandır (`web.css` onu bütöv import edir) — burada YALNIZ brauzer
 *   həndəsəsi `web.css`-də üstündən yazılır. Yəni bir rəng dəyişəndə hər iki səth
 *   dəyişir.
 * ⚠ HERO YOXDUR — cihaz uşaq ekranındakı eyni qərar (sadəlik; izahı orada).
 */
export function createWebKidsHomeScreen(
  home: Home,
  recommended: ContentCard[],
  continueWatching: ContinueCard[],
  watchlist: ContentCard[],
): Screen {
  const page = webPage()

  const body = el('div')
  body.style.padding = '0 var(--web-pad)'
  page.appendChild(body)

  // ── Salamlama ───────────────────────────────────────────────────────────
  const name = readSelectedProfileName()
  const hello = el('div', 'kidshello')
  hello.appendChild(el('div', 'kidshello__avatar', (name || '?').charAt(0).toUpperCase()))
  const helloText = el('div', 'kidshello__text')
  /* ⚠ «UŞAQ» NİŞANI: rəng tək başına kifayət deyil — rejim SÖZLƏ də yazılır (sahib
     tələbi «bilinsin kids profili olmağı»). Sayt, iOS və Android-də eyni nişan var. */
  helloText.appendChild(el('span', 'kidshello__badge', T('Uşaq')))
  helloText.appendChild(
    el('p', 'kidshello__title', name ? T('Salam, {name}!').replace('{name}', name) : T('Salam!')),
  )
  helloText.appendChild(el('p', 'kidshello__sub', T('Bu gün nə baxırıq?')))
  hello.appendChild(helloText)
  body.appendChild(hello)

  // ── «Nə baxaq?» ─────────────────────────────────────────────────────────
  const cats = el('div', 'web-kidscats')
  cats.appendChild(el('h2', 'web-row__title kidstitle kidstitle--animation', T('Nə baxaq?')))
  const catsTrack = el('div', 'web-kidscats__track')
  for (let c = 0; c < KIDS_CATEGORIES.length; c++) {
    const category = KIDS_CATEGORIES[c]
    if (!category) continue
    const slug = category.slug
    const title = T(category.title)
    // ⚠ `<button>`-dur, cihazdakı `<div>` deyil: brauzer variantında hər aksiya
    //   doğma `:focus` və Tab yolunda olmalıdır (`browser/ui.ts` qaydası).
    const tile = document.createElement('button')
    tile.type = 'button'
    tile.className = 'kidscat kidscat--' + slug + ' web-focusable'
    tile.appendChild(kidsCategoryIcon(category.icon))
    tile.appendChild(el('span', 'kidscat__label', title))
    tile.onclick = () => navigate({ name: 'list', kind: 'genre', slug, title })
    catsTrack.appendChild(tile)
  }
  cats.appendChild(catsTrack)
  body.appendChild(cats)

  // ── Sıralar — cihaz uşaq ekranı ilə EYNİ ardıcıllıq ──────────────────────
  const sources: Array<{ title: string; cards: HTMLElement[]; slug?: string }> = []

  if (continueWatching.length) {
    sources.push({
      title: T('Davam et'),
      cards: continueWatching.map((item) => webCard(item.card, open, { progress: item.progress })),
    })
  }
  if (recommended.length) {
    sources.push({ title: T('Sizin üçün seçilənlər'), cards: recommended.map((card) => webCard(card, open)) })
  }
  if (watchlist.length) {
    sources.push({ title: T('Sonra İzlə'), cards: watchlist.map((card) => webCard(card, open)) })
  }
  for (let i = 0; i < home.blocks.length; i++) {
    const block = home.blocks[i]
    if (!block || !block.items.length) continue
    sources.push({ title: block.title, cards: block.items.map((card) => webCard(card, open)), slug: block.slug })
  }

  for (let i = 0; i < sources.length; i++) {
    const source = sources[i]
    if (!source) continue
    const slug = source.slug
    const title = source.title
    const row = webRow(
      title,
      source.cards,
      slug ? { label: T('Hamısı ›'), onClick: () => navigate({ name: 'list', kind: 'collection', slug, title }) } : undefined,
    )
    // Sıranın rəngi — indekslə dövr edir (`KIDS_ROW_TONES`); dəyərlər `tv.css`-də.
    const tone = KIDS_ROW_TONES[i % KIDS_ROW_TONES.length]
    row.className += ' kidsrow kidsrow--' + tone
    const heading = row.getElementsByClassName('web-row__title')[0]
    if (heading) heading.className += ' kidstitle kidstitle--' + tone
    body.appendChild(row)
  }

  // ── Boş vəziyyət — qara ekran QADAĞANDIR (Frontend/CLAUDE.md §6) ────────
  if (!sources.length) body.appendChild(webEmpty(T('Sənin üçün filmlər hazırlanır. Tezliklə burada olacaq.')))

  return webScreen(page)
}

/** Kart açılışı — adi ana ekrandakı eyni qərar (film MODALI, `browser/detailModal.ts`). */
function open(card: ContentCard): void {
  openWebDetail(card)
}
